import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { ARCHETYPE_COLORS, CAREER_STAGE_LABELS, Profile } from '../types';

function exportAthletesCSV(athletes: Profile[]) {
  if (!athletes.length) return;
  const s1 = (a: Profile) => a.assessment_status === 'completed';
  const s2 = (a: Profile) => a.transition_analysis != null;
  const rows = athletes.map(a => {
    const active = s1(a) && s2(a);
    return {
      'Name': a.full_name || a.username || '',
      'Sport': a.sport || '',
      'Career Stage': CAREER_STAGE_LABELS[a.career_stage || ''] || a.career_stage || '',
      'Archetype': a.athlete_archetype || '',
      'Stage 1 — Identity': s1(a) ? 'Completed' : 'Pending',
      'Stage 2 — Transition': s2(a) ? 'Completed' : s1(a) ? 'In Progress' : '—',
      'Stage 3 — Leadership': active ? 'Active' : '—',
      'Stage 4 — Legacy': active ? 'Active' : '—',
      'Subscription': a.subscription_tier || '',
      'Last Active': a.updated_at ? new Date(a.updated_at).toLocaleDateString('en-CA') : '',
    };
  });
  const headers = Object.keys(rows[0]);
  const esc = (v: any) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc((r as any)[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `bthd-athletes-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

function initials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function formatDate(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeAgo(ts: string | null): string {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return formatDate(ts);
}

// Athlete Detail Panel
function AthleteDetail({ athlete, onClose }: { athlete: Profile; onClose: () => void }) {
  const archetypeColor = athlete.athlete_archetype ? (ARCHETYPE_COLORS[athlete.athlete_archetype] || '#4a6080') : '#4a6080';
  const isComplete = athlete.assessment_status === 'completed';
  const strengths = athlete.transition_analysis?.strengths;
  const [institutionName, setInstitutionName] = useState<string | null>(null);

  useEffect(() => {
    if (!athlete.institution_id) return;
    supabase.from('institutions').select('name').eq('id', athlete.institution_id).single()
      .then(({ data }) => { if (data) setInstitutionName(data.name); });
  }, [athlete.institution_id]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
    }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />

      {/* Panel */}
      <div style={{
        position: 'relative', width: 420, background: 'var(--surface)',
        borderLeft: '1px solid var(--border)', overflowY: 'auto', zIndex: 1,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: archetypeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 17, color: '#fff', flexShrink: 0 }}>
            {initials(athlete.full_name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)', lineHeight: 1.1 }}>
              {athlete.full_name || athlete.username || 'Unknown Athlete'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
              {athlete.sport || 'Sport not set'}{athlete.position ? ` · ${athlete.position}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 20, padding: '4px 8px', borderRadius: 6 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', flex: 1 }}>

          {/* Status badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {athlete.athlete_archetype && (
              <span style={{ padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${archetypeColor}44`, background: `${archetypeColor}18`, color: archetypeColor, fontSize: 11, fontWeight: 700 }}>
                {athlete.athlete_archetype}
              </span>
            )}
            <span style={{ padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${isComplete ? '#0fa86d44' : '#e88c0e44'}`, background: `${isComplete ? '#0fa86d' : '#e88c0e'}18`, color: isComplete ? '#0fa86d' : '#e88c0e', fontSize: 11, fontWeight: 700 }}>
              {isComplete ? '✓ Assessment Complete' : '○ Not Assessed'}
            </span>
            {athlete.subscription_tier && (
              <span style={{ padding: '4px 10px', borderRadius: 20, border: '1.5px solid rgba(1,76,141,0.3)', background: 'rgba(1,76,141,0.1)', color: '#5b9bd5', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                {athlete.subscription_tier}
              </span>
            )}
          </div>

          {/* Profile fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FieldRow label="Career Stage" value={athlete.career_stage ? (CAREER_STAGE_LABELS[athlete.career_stage] || athlete.career_stage) : null} />
            <FieldRow label="Sport" value={athlete.sport} />
            <FieldRow label="Position" value={athlete.position || athlete.sport_position} />
            <FieldRow label="Username" value={athlete.username ? `@${athlete.username}` : null} />
            <FieldRow label="Institution" value={institutionName || athlete.institution_id || null} />
            <FieldRow
              label="Career Analysis Score"
              value={athlete.translation_score != null && athlete.translation_score > 0 ? String(athlete.translation_score) : 'Not started'}
              note="Populated when athlete completes the BTHD mobile app journey"
            />
            <FieldRow label="Joined" value={formatDate(athlete.updated_at)} />
            <FieldRow label="Assessment Completed" value={athlete.completed_at ? formatDate(athlete.completed_at) : null} />
            <FieldRow label="Last Analysis" value={athlete.last_analysis_at ? timeAgo(athlete.last_analysis_at) : null} />
          </div>

          {/* Strength Scores — if available in transition_analysis */}
          {strengths && typeof strengths === 'object' && (
            <div style={{ marginTop: 22 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 12 }}>Strength Scores</div>
              {Object.entries(strengths).map(([dim, score]) => (
                <div key={dim} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', width: 100, flexShrink: 0, textTransform: 'capitalize' }}>{dim}</div>
                  <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${score as number}%`, background: archetypeColor, borderRadius: 10 }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', width: 28, textAlign: 'right' }}>{score as number}</div>
                </div>
              ))}
            </div>
          )}

          {/* Bio */}
          {athlete.bio && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>Bio</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, background: 'var(--surface-2)', borderRadius: 9, padding: '12px 14px' }}>{athlete.bio}</div>
            </div>
          )}

          {/* Social links */}
          {(athlete.linkedin_url || athlete.instagram_url) && (
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              {athlete.linkedin_url && (
                <a href={athlete.linkedin_url} target="_blank" rel="noreferrer" style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(1,76,141,0.12)', border: '1px solid rgba(1,76,141,0.25)', color: '#5b9bd5', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>LinkedIn →</a>
              )}
              {athlete.instagram_url && (
                <a href={athlete.instagram_url} target="_blank" rel="noreferrer" style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(232,140,14,0.1)', border: '1px solid rgba(232,140,14,0.25)', color: '#e88c0e', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Instagram →</a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value, note }: { label: string; value: string | null | undefined; note?: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, width: 130, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: value ? 'var(--text)' : 'var(--text-3)', fontStyle: value ? 'normal' : 'italic' }}>{value || 'Not set'}</div>
        {note && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.4 }}>{note}</div>}
      </div>
    </div>
  );
}

// Athlete Card
function AthleteCard({ profile, onClick }: { profile: Profile; onClick: () => void }) {
  const archetypeColor = profile.athlete_archetype ? (ARCHETYPE_COLORS[profile.athlete_archetype] || '#4a6080') : '#4a6080';
  const isComplete = profile.assessment_status === 'completed';

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)',
        padding: '16px', cursor: 'pointer', transition: 'border-color 0.15s',
        position: 'relative', overflow: 'hidden',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = archetypeColor + '66'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: archetypeColor }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: archetypeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0 }}>
          {initials(profile.full_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profile.full_name || profile.username || 'Anonymous'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
            {profile.sport || 'Sport not set'}{profile.position ? ` · ${profile.position}` : ''}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {profile.athlete_archetype ? (
          <span style={{ padding: '3px 8px', borderRadius: 12, background: archetypeColor + '22', color: archetypeColor, fontSize: 10, fontWeight: 700 }}>
            {profile.athlete_archetype}
          </span>
        ) : (
          <span style={{ padding: '3px 8px', borderRadius: 12, background: 'var(--surface-2)', color: 'var(--text-3)', fontSize: 10, fontWeight: 600, fontStyle: 'italic' }}>
            Not assessed
          </span>
        )}
        {profile.career_stage && (
          <span style={{ padding: '3px 8px', borderRadius: 12, background: 'rgba(1,76,141,0.1)', color: '#5b9bd5', fontSize: 10, fontWeight: 600 }}>
            {CAREER_STAGE_LABELS[profile.career_stage] || profile.career_stage}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: isComplete ? '#0fa86d' : '#e88c0e' }} />
          <span style={{ fontSize: 11, color: isComplete ? '#0fa86d' : '#e88c0e', fontWeight: 600 }}>
            {isComplete ? 'Assessed' : 'Pending'}
          </span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{timeAgo(profile.updated_at)}</span>
      </div>
    </div>
  );
}

const SPORT_OPTIONS = ['Basketball', 'Football', 'Soccer', 'Hockey', 'Baseball', 'Track & Field', 'Swimming', 'Tennis'];
const STAGE_OPTIONS = ['amateur', 'professional', 'retiring', 'retired'];
const ARCHETYPE_OPTIONS = ['Commander', 'Architect', 'Catalyst'];

export default function AthletesPage({ institutionId }: { institutionId?: string | null }) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSport, setFilterSport] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterArchetype, setFilterArchetype] = useState('');
  const [selected, setSelected] = useState<Profile | null>(null);

  useEffect(() => {
    let q = supabase
      .from('profiles')
      .select('id,full_name,username,sport,position,sport_position,career_stage,athlete_archetype,assessment_status,subscription_tier,institution_id,institution_type,is_admin,updated_at,completed_at,translation_score,bio,linkedin_url,instagram_url,avatar_url,transition_analysis,last_analysis_at')
      .eq('is_admin', false).eq('user_type', 'athlete')
      .order('updated_at', { ascending: false });
    if (institutionId) q = q.eq('institution_id', institutionId);
    q.then(({ data }) => {
      setProfiles((data as Profile[]) ?? []);
      setLoading(false);
    });
  }, [institutionId]);

  const filtered = profiles.filter(p => {
    const name = (p.full_name || p.username || '').toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (filterSport && !(p.sport || '').toLowerCase().includes(filterSport.toLowerCase())) return false;
    if (filterStage && p.career_stage !== filterStage) return false;
    if (filterArchetype && p.athlete_archetype !== filterArchetype) return false;
    return true;
  });

  const hasFilters = search || filterSport || filterStage || filterArchetype;

  const selectStyle: React.CSSProperties = {
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    color: 'var(--text)', borderRadius: 8, padding: '7px 10px',
    fontSize: 12, cursor: 'pointer', outline: 'none',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>Athletes</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
            {loading ? 'Loading...' : `${filtered.length} of ${profiles.length} athletes`}
          </div>
        </div>
        {!loading && filtered.length > 0 && (
          <button
            onClick={() => exportAthletesCSV(filtered)}
            style={{
              padding: '8px 16px', borderRadius: 8, background: 'transparent',
              border: '1px solid var(--border)', color: 'var(--text-2)',
              fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
              fontSize: 13, cursor: 'pointer', letterSpacing: 0.5,
            }}
          >
            ↓ Export CSV ({filtered.length})
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name..."
          style={{ ...selectStyle, flex: '1 1 180px', minWidth: 160 }}
        />
        <select value={filterSport} onChange={e => setFilterSport(e.target.value)} style={selectStyle}>
          <option value="">All Sports</option>
          {SPORT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterStage} onChange={e => setFilterStage(e.target.value)} style={selectStyle}>
          <option value="">All Career Stages</option>
          {STAGE_OPTIONS.map(s => <option key={s} value={s}>{CAREER_STAGE_LABELS[s]}</option>)}
        </select>
        <select value={filterArchetype} onChange={e => setFilterArchetype(e.target.value)} style={selectStyle}>
          <option value="">All Archetypes</option>
          {ARCHETYPE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterSport(''); setFilterStage(''); setFilterArchetype(''); }}>
            Clear
          </button>
        )}
      </div>

      {/* Grid of cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading athletes...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          {hasFilters ? 'No athletes match these filters.' : 'No athletes yet.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {filtered.map(p => (
            <AthleteCard key={p.id} profile={p} onClick={() => setSelected(p)} />
          ))}
        </div>
      )}

      {/* Detail panel */}
      {selected && <AthleteDetail athlete={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
